import React, { useEffect } from "react";
import { useMobileEdgeChromeStore } from "../../stores/mobileEdgeChromeStore";

export function useMainPanelEventSubscriptions({
  isMobilePanelViewport,
  isCollapsed,
  anyMainPanelPopupOpen,
  mobilePanelCoversMenu,
  closeMainPanelPopups,
  setIsCollapsed,
  setSceneGraphOpen,
  setSceneGraphOpenFromMenu,
  setPropertiesPanelOpen,
  selectTendon,
  selectEquality,
  setLastSelectionSource,
  setCameraProbeOpenFromMenu,
  setSensorsOpenFromMenu,
}) {
  React.useEffect(() => {
    if (!isMobilePanelViewport) return undefined;
    const onBlankTap = () => {
      setIsCollapsed(true);
      closeMainPanelPopups();
      window.dispatchEvent(new CustomEvent("close-properties-panel"));
    };
    window.addEventListener("mobile-viewport-blank-tap", onBlankTap);
    return () => window.removeEventListener("mobile-viewport-blank-tap", onBlankTap);
  }, [closeMainPanelPopups, isMobilePanelViewport, setIsCollapsed]);

  useEffect(() => {
    const handleOpen = () => setSceneGraphOpenFromMenu(true);
    const handleClose = () => setSceneGraphOpen(false);
    window.addEventListener("open-body-panel", handleOpen);
    window.addEventListener("close-body-panel", handleClose);
    return () => {
      window.removeEventListener("open-body-panel", handleOpen);
      window.removeEventListener("close-body-panel", handleClose);
    };
  }, [setSceneGraphOpen, setSceneGraphOpenFromMenu]);

  useEffect(() => {
    const handlePropertiesPanelState = (event) => {
      const minimized = event?.detail?.minimized !== false;
      setPropertiesPanelOpen(!minimized);
    };
    window.addEventListener("properties-panel-minimized-change", handlePropertiesPanelState);
    window.dispatchEvent(new CustomEvent("request-properties-panel-minimized-state"));
    return () =>
      window.removeEventListener("properties-panel-minimized-change", handlePropertiesPanelState);
  }, [setPropertiesPanelOpen]);

  useEffect(() => {
    const handleOpenSceneElementEditor = (event) => {
      const detail = event?.detail || {};
      const type = detail.type;
      const uuid = detail.uuid || null;

      if (type === "tendon" || type === "equality") {
        if (!uuid) return;
        setLastSelectionSource("sceneTree");
        if (type === "tendon") selectTendon(uuid);
        else selectEquality(uuid);
        window.dispatchEvent(new CustomEvent("open-properties-panel"));
      }
    };

    window.addEventListener("open-scene-element-editor", handleOpenSceneElementEditor);
    return () => {
      window.removeEventListener("open-scene-element-editor", handleOpenSceneElementEditor);
    };
  }, [selectEquality, selectTendon, setLastSelectionSource]);

  useEffect(() => {
    const handleOpenCameraProbe = () => setCameraProbeOpenFromMenu(true);
    const handleOpenSensors = () => setSensorsOpenFromMenu(true);
    window.addEventListener("open-camera-probe", handleOpenCameraProbe);
    window.addEventListener("open-sensors-panel", handleOpenSensors);
    return () => {
      window.removeEventListener("open-camera-probe", handleOpenCameraProbe);
      window.removeEventListener("open-sensors-panel", handleOpenSensors);
    };
  }, [setCameraProbeOpenFromMenu, setSensorsOpenFromMenu]);

  useEffect(() => {
    if (!isMobilePanelViewport) return undefined;
    const handleOpenInspector = () => {
      closeMainPanelPopups();
    };
    window.addEventListener("open-properties-panel", handleOpenInspector);
    return () => window.removeEventListener("open-properties-panel", handleOpenInspector);
  }, [isMobilePanelViewport, closeMainPanelPopups]);

  const prevAnyMainPanelPopupOpenRef = React.useRef(anyMainPanelPopupOpen);
  React.useEffect(() => {
    if (!isMobilePanelViewport) {
      prevAnyMainPanelPopupOpenRef.current = anyMainPanelPopupOpen;
      return;
    }
    const hadPopup = prevAnyMainPanelPopupOpenRef.current;
    prevAnyMainPanelPopupOpenRef.current = anyMainPanelPopupOpen;
    if (hadPopup && !anyMainPanelPopupOpen) {
      setIsCollapsed(true);
    }
  }, [anyMainPanelPopupOpen, isMobilePanelViewport, setIsCollapsed]);

  useEffect(() => {
    const { actions } = useMobileEdgeChromeStore.getState();
    actions.setMobileMainMenuExpanded(isMobilePanelViewport && !isCollapsed);
  }, [isMobilePanelViewport, isCollapsed]);

  useEffect(() => {
    useMobileEdgeChromeStore
      .getState()
      .actions.setMobilePanelCoversEdgeChrome(mobilePanelCoversMenu);
  }, [mobilePanelCoversMenu]);
}
